import React, { useState, useEffect } from "react";
import { MdAdd, MdEdit, MdDelete, MdLink } from "react-icons/md";
import Card from "components/card";
import { toast } from "react-toastify";
import { adminApiService } from "services/api";

const SocialMedia = () => {
  const [socialLinks, setSocialLinks] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    link: "",
  });
  const [editingId, setEditingId] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    setIsLoading(true);
    try {
      const response = await adminApiService.getSocialMedia();
      if (!response.ok) throw new Error("Failed to fetch social media links");
      const data = await response.json();
      setSocialLinks(data);
    } catch (error) {
      console.error("Error fetching social media links:", error);
      toast.error("Failed to load social media links");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: "", link: "" });
    setEditingId(false);
  };

  const handleAddLink = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await adminApiService.addSocialMedia(formData);

      if (!response.ok) throw new Error("Failed to add social media link");

      toast.success("Social media link added successfully");
      resetForm();
      fetchSocialLinks();
    } catch (error) {
      console.error("Error adding social media link:", error);
      toast.error("Failed to add social media link");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateLink = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await adminApiService.updateSocialMedia(
        editingId,
        formData
      );

      if (!response.ok) throw new Error("Failed to update social media link");

      toast.success("Social media link updated successfully");
      resetForm();
      fetchSocialLinks();
    } catch (error) {
      console.error("Error updating social media link:", error);
      toast.error("Failed to update social media link");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (link) => {
    setFormData({
      name: link.name,
      link: link.link,
    });
    console.log(link);

    setEditingId(link.id);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this social media link?")
    )
      return;

    setIsLoading(true);
    try {
      const response = await adminApiService.deleteSocialMedia(id);

      if (!response.ok) throw new Error("Failed to delete social media link");

      toast.success("Social media link deleted successfully");
      fetchSocialLinks();
    } catch (error) {
      console.error("Error deleting social media link:", error);
      toast.error("Failed to delete social media link");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2">
      {/* Form Card */}
      <Card extra="w-full p-6">
        <div className="mb-6 flex items-center justify-between">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            {editingId ? "Edit Link" : "Add New Link"}
          </h4>
          {editingId && (
            <button
              onClick={resetForm}
              className="rounded-lg px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700"
            >
              Cancel
            </button>
          )}
        </div>

        <form onSubmit={editingId ? handleUpdateLink : handleAddLink}>
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Platform Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-primary focus:ring-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-400"
              placeholder="e.g. Twitter, LinkedIn"
              required
            />
          </div>
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Profile URL
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MdLink className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                className="block w-full rounded-lg border border-gray-300 p-2.5 pl-10 text-sm focus:border-primary focus:ring-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:placeholder-gray-400"
                placeholder="https://example.com/your-profile"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="linear flex w-full items-center justify-center rounded-xl bg-primary py-3 text-base font-medium text-white transition duration-200 hover:bg-primary/90 active:bg-primary/80 disabled:opacity-70"
          >
            {isLoading ? (
              <span>Processing...</span>
            ) : editingId ? (
              <>
                <MdEdit className="mr-2" />
                Update Link
              </>
            ) : (
              <>
                <MdAdd className="mr-2" />
                Add Link
              </>
            )}
          </button>
        </form>
      </Card>

      {/* Links List Card */}
      <Card extra="w-full p-6">
        <h4 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
          Your Social Links
        </h4>

        {isLoading && !socialLinks.length ? (
          <div className="flex justify-center py-8">
            <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-primary"></div>
          </div>
        ) : socialLinks.length === 0 ? (
          <div className="rounded-lg bg-gray-50 p-6 text-center dark:bg-navy-700">
            <p className="text-gray-500 dark:text-gray-400">
              No social media links added yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {socialLinks.map((link) => (
              <div
                key={link._id}
                className="flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-gray-700"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-navy-700">
                    <MdLink className="h-5 w-5 text-gray-600 dark:text-white" />
                  </div>
                  <div>
                    <h5 className="font-medium text-navy-700 dark:text-white">
                      {link.name}
                    </h5>
                    <a
                      href={link.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      {link.link.length > 30
                        ? `${link.link.substring(0, 30)}...`
                        : link.link}
                    </a>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(link)}
                    disabled={isLoading}
                    className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700"
                    title="Edit"
                  >
                    <MdEdit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(link.id)}
                    disabled={isLoading}
                    className="rounded-lg p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Delete"
                  >
                    <MdDelete className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SocialMedia;
